import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { professionsAPI } from '../../services/api';

// Асинхронные экшены
export const fetchProfessions = createAsyncThunk(
  'professions/fetchProfessions',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await professionsAPI.getAll(params);
      return {
        data: response.data,
        params
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchProfessionGroups = createAsyncThunk(
  'professions/fetchProfessionGroups',
  async (params = { all: true }, { rejectWithValue }) => {
    try {
      const response = await professionsAPI.getAll(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchProfessionById = createAsyncThunk(
  'professions/fetchProfessionById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await professionsAPI.getById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Slice
const professionsSlice = createSlice({
  name: 'professions',
  initialState: {
    items: [],
    groups: [], // Отдельный массив для групп профессий
    selectedProfession: null,
    loading: false,
    groupsLoading: false,
    error: null,
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
      pages: 1
    }
  },
  reducers: {
    // Синхронное обновление профессии (используется при получении событий)
    updateProfession: (state, action) => {
      const profession = action.payload;
      const index = state.items.findIndex(item => item.id === profession.id);
      
      if (index !== -1) {
        // Обновление существующей профессии
        state.items[index] = {
          ...state.items[index],
          ...profession,
        };
      } else {
        // Добавление новой профессии
        state.items.push(profession);
      }
      
      // Также обновляем в группах, если профессия там есть
      const groupIndex = state.groups.findIndex(item => item.id === profession.id);
      if (groupIndex !== -1) {
        state.groups[groupIndex] = {
          ...state.groups[groupIndex],
          ...profession,
        };
      } else {
        // Добавляем новую профессию в список групп, если её там нет
        // Это позволит сразу использовать новую профессию как группу
        state.groups.push(profession);
      }
      
      // Если открыта детальная страница профессии, обновляем и её
      if (state.selectedProfession && state.selectedProfession.id === profession.id) {
        state.selectedProfession = {
          ...state.selectedProfession,
          ...profession,
        };
      }
    },
    
    // Удаление профессии (используется при получении событий)
    deleteProfession: (state, action) => {
      const id = action.payload;
      // Преобразуем id к строке для надежного сравнения
      const idStr = id?.toString();
      
      if (!idStr) {
        console.error('Редьюсер: получен пустой ID для удаления профессии');
        return;
      }
      
      // Фильтруем элементы, сравнивая строковые представления ID
      state.items = state.items.filter(item => {
        const itemId = item.id?.toString();
        const result = itemId !== idStr;
        if (!result) {
          console.log(`Редьюсер: удаляем профессию с ID ${itemId}`);
        }
        return result;
      });
      
      // Также удаляем из групп
      state.groups = state.groups.filter(item => {
        const itemId = item.id?.toString();
        return itemId !== idStr;
      });
      
      // Если открыта детальная страница удаляемой профессии, очищаем её
      if (state.selectedProfession && state.selectedProfession.id?.toString() === idStr) {
        state.selectedProfession = null;
      }
    },
    
    // Сброс выбранной профессии
    resetSelectedProfession: (state) => {
      state.selectedProfession = null;
    },
    
    // Обновление параметров пагинации
    setPagination: (state, action) => {
      state.pagination = {
        ...state.pagination,
        ...action.payload
      };
    }
  },
  extraReducers: (builder) => {
    builder
      // Обработка fetchProfessions
      .addCase(fetchProfessions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProfessions.fulfilled, (state, action) => {
        const { data, params } = action.payload;
        
        // Обработка разных форматов ответа API
        if (data && data.data && Array.isArray(data.data)) {
          // Новый формат с вложенными данными и пагинацией
          state.items = data.data;
          
          // Обновляем пагинацию, если она есть
          if (data.pagination) {
            state.pagination = {
              page: params.page || 1,
              limit: params.limit || 10,
              total: data.pagination.total || 0,
              pages: Math.ceil(data.pagination.total / (params.limit || 10)) || 1
            };
          }
        } else if (Array.isArray(data)) {
          // Старый формат - прямой массив
          state.items = data;
          state.pagination = {
            ...state.pagination,
            total: data.length,
            pages: Math.ceil(data.length / (params.limit || 10)) || 1
          };
        }
        
        state.loading = false;
      })
      .addCase(fetchProfessions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Не удалось загрузить профессии';
      })
      
      // Обработка fetchProfessionGroups
      .addCase(fetchProfessionGroups.pending, (state) => {
        state.groupsLoading = true;
        state.error = null;
      })
      .addCase(fetchProfessionGroups.fulfilled, (state, action) => {
        const data = action.payload;
        
        // Обработка разных форматов ответа API
        if (data && data.data && Array.isArray(data.data)) {
          state.groups = data.data;
        } else if (Array.isArray(data)) {
          state.groups = data;
        } else {
          state.groups = [];
        }
        
        state.groupsLoading = false;
      })
      .addCase(fetchProfessionGroups.rejected, (state, action) => {
        state.groupsLoading = false;
        state.error = action.payload || 'Не удалось загрузить группы профессий';
      })
      
      // Обработка fetchProfessionById
      .addCase(fetchProfessionById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProfessionById.fulfilled, (state, action) => {
        state.selectedProfession = action.payload;
        state.loading = false;
      })
      .addCase(fetchProfessionById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Не удалось загрузить профессию';
      });
  },
});

// Экспорт экшенов
export const { 
  updateProfession, 
  deleteProfession, 
  resetSelectedProfession,
  setPagination
} = professionsSlice.actions;

// Селекторы
export const selectAllProfessions = (state) => state.professions.items;
export const selectProfessionGroups = (state) => state.professions.groups;
export const selectSelectedProfession = (state) => state.professions.selectedProfession;
export const selectProfessionsLoading = (state) => state.professions.loading;
export const selectProfessionGroupsLoading = (state) => state.professions.groupsLoading;
export const selectProfessionsError = (state) => state.professions.error;
export const selectProfessionsPagination = (state) => state.professions.pagination;

// Экспорт редьюсера
export default professionsSlice.reducer; 